export interface SseMessage {
  id?: string;
  event?: string;
  data: string;
}

export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onMessage: (message: SseMessage) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });

      let boundary = findFrameBoundary(buffer);
      while (boundary) {
        const frame = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary.length);
        const message = parseFrame(frame);
        if (message) onMessage(message);
        boundary = findFrameBoundary(buffer);
      }

      if (done) break;
    }

    const finalMessage = parseFrame(buffer);
    if (finalMessage) onMessage(finalMessage);
  } finally {
    reader.releaseLock();
  }
}

export function supportErrorMessage(
  value: Record<string, unknown>,
  fallback = 'An unexpected error occurred',
): string {
  const details = isRecord(value.details) ? value.details : undefined;
  const message = typeof value.message === 'string' ? value.message : fallback;
  const errorId = typeof value.errorId === 'string' ? value.errorId : details?.errorId;
  return typeof errorId === 'string' ? `${message} (Error ID: ${errorId})` : message;
}

function findFrameBoundary(value: string): { index: number; length: number } | undefined {
  const match = /\r?\n\r?\n/.exec(value);
  return match ? { index: match.index, length: match[0].length } : undefined;
}

function parseFrame(frame: string): SseMessage | undefined {
  let id: string | undefined;
  let event: string | undefined;
  const data: string[] = [];

  for (const line of frame.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue;
    const separator = line.indexOf(':');
    const field = separator < 0 ? line : line.slice(0, separator);
    const rawValue = separator < 0 ? '' : line.slice(separator + 1);
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;
    if (field === 'id') id = value;
    else if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }

  return data.length > 0 ? { id, event, data: data.join('\n') } : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}